import React from "react";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import { Button } from "@mantine/core";
import { IconSettings } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import { showNotification } from "@mantine/notifications";
import FormConfirmation from "./formConfirmation";
import ServiceTypeCompleteSummary from "./orderService/ServiceTypeCompleteSummary";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mantine/dates";
import { IconExclamationMark } from "@tabler/icons";
import { useAuthUser } from "react-auth-kit";
import { useTabActionsOptional, notifyListRefresh } from "../context/tabContext";
import {
  normalizePathname,
  refreshOpenDetailTab,
} from "../utils/tabNavigation";

const OrderServiceCompleteButton = ({ mainForm }) => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const tabActions = useTabActionsOptional();

  const getOrderLink = () => {
    const { userRole } = auth() ?? {};
    return (
      userRole?.entityLinks?.find((l) => l.entity === "order")?.link ??
      "/gi/order"
    );
  };

  const formSchema = {
    name: "CompleteOrderServiceRequest",
    title: "Complete Order Service",
    description: "Please follow the steps to complete the service",

    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px",
    },

    apiEntity: "orderService",
    initialValues: {},
    validate: {},
    showSaveBar: false,

    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default",
            defaultValue: "Service Info",
            orientation: "vertical",
            style: { minHeight: "400px" },
          },
          steps: [
            { key: "t1-1", label: "Service Info", icon: IconSettings },
            { key: "t1-2", label: "Order Updates", icon: IconSettings },
            { key: "t1-7", label: "Confirmation", icon: IconSettings },
          ],
        },
        {
          key: "section-info",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Service Information",
            withBorder: false,
            mt: 0,
            ml: 20,
          },
        },
        {
          key: "section-summary",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Order Field Updates",
            withBorder: false,
            mt: 0,
            ml: 20,
          },
        },
        {
          key: "section-confirm",
          parent: "t1-7",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Complete Confirmation",
            withBorder: false,
            mt: 0,
            ml: 20,
          },
        },
      ],

      fields: [
        {
          name: "receieveDate",
          parent: "section-info",
          component: DatePicker,
          type: "datePicker",
          props: {
            label: "Receive Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "submissionDate",
          parent: "section-info",
          component: DatePicker,
          type: "datePicker",
          props: {
            label: "Submission Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "completeDate",
          parent: "section-info",
          component: DatePicker,
          type: "datePicker",
          props: {
            label: "Completion Date",
            required: true,
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "completeSummary",
          component: ServiceTypeCompleteSummary,
          parent: "section-summary",
          props: {},
        },
        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {
            successText: [
              "Please ONLY click the button when you confirm the order service is completed.",
            ],
          },
        },
      ],
    },
  };

  const onSubmit = async ({ values }) => {
    try {
      const data = await api.OrderService.completeService({
        id: mainForm.values._id,
        submissionDate: values.submissionDate,
        receieveDate: values.receieveDate,
        completeDate: values.completeDate ?? moment().toDate(),
      });

      if (!data?.orderService) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Complete service error",
          message: "Failed to complete service.",
        });
        return;
      }

      await mainForm.reload?.();

      const orderId =
        data.order?._id ??
        mainForm.values.orderId?._id ??
        mainForm.values.orderId;
      if (orderId) {
        const orderLink = getOrderLink();
        refreshOpenDetailTab(tabActions, orderLink, String(orderId));
        notifyListRefresh(normalizePathname(orderLink));
      }
      mainForm.notifyListRefresh?.();

      formAction.close();

      showNotification({
        title: "Service completed",
        message: data.order
          ? "Service completed and order updated."
          : "Service completed.",
      });
    } catch (error) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Complete service error",
        message:
          error?.data?.error?.message ??
          error?.data?.error ??
          error?.message ??
          "Failed to complete service",
      });
    }
  };

  const [renderForm, formAction] = useFormRender(formSchema, null, onSubmit);

  const handleActionClick = () => {
    const id = mainForm?.values._id;
    if (!id) return;
    formAction.open({
      mode: "edit",
      id,
      data: {
        ...mainForm.values,
        completeDate: mainForm.values.completeDate ?? new Date(),
      },
    });
  };

  return (
    <>
      <Button fullWidth onClick={handleActionClick} color="green">
        {t("Complete Service")}
      </Button>
      {renderForm()}
    </>
  );
};

export default OrderServiceCompleteButton;
